import { Check, Images } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import { CookSteps } from '@/components/recipes/CookSteps'
import { Button } from '@/components/ui/button'
import { photoUrl } from '@/lib/photos'
import { cardPhotos, lines, useRecipe } from '@/lib/recipes'
import { useMe } from '@/lib/useMe'
import { cn } from '@/lib/utils'
import { PageLoader } from '@/components/Loader'

// Large type for reading at the counter, with the screen kept awake where the browser allows it. Ticking off
// ingredients is just for this sitting and isn't saved.
export function CookView() {
  const { id = '' } = useParams()
  const recipe = useRecipe(id)
  const [checked, setChecked] = useState<number[]>([])
  const [showCard, setShowCard] = useState(false)
  const awake = useWakeLock()
  const modern = useMe().data?.recipesStyle === 'modern'

  if (recipe.isPending) return <PageLoader />
  if (recipe.isError) return <FormError error={recipe.error} />
  if (modern) return <CookSteps recipe={recipe.data} awake={awake} />
  const { title } = recipe.data
  const photos = cardPhotos(recipe.data)
  const ingredients = lines(recipe.data.ingredients)
  const steps = lines(recipe.data.steps)
  const toggle = (i: number) => setChecked((c) => (c.includes(i) ? c.filter((x) => x !== i) : [...c, i]))

  return (
    <article className="flex flex-col gap-8 text-lg leading-relaxed">
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-display text-balance">{title}</h1>
        <Button asChild variant="outline" size="lg" className="shrink-0">
          <Link to={`/recipe/${id}`}>Done</Link>
        </Button>
      </div>

      {photos.length > 0 && (
        <div className="flex flex-col gap-3">
          <Button variant="secondary" size="lg" className="self-start" onClick={() => setShowCard((s) => !s)}>
            <Images aria-hidden /> {showCard ? 'Hide the card' : 'Show the card'}
          </Button>
          {showCard && (
            <div className="flex flex-col gap-3 rounded-[2rem] bg-secondary p-3 sm:p-5">
              {photos.map((photo, i) => (
                <img
                  key={photo.id}
                  src={photoUrl(photo.ref, 'display')}
                  width={photo.width}
                  height={photo.height}
                  alt={`The card for ${title}, photo ${i + 1} of ${photos.length}`}
                  className="h-auto w-full rounded-xl shadow-paper"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {ingredients.length > 0 && (
        <section aria-labelledby="cook-ingredients" className="flex flex-col gap-3">
          <h2 id="cook-ingredients" className="text-title">
            Ingredients
          </h2>
          <ul className="flex flex-col">
            {ingredients.map((line, i) => (
              <li key={i}>
                <label className="flex min-h-12 cursor-pointer items-start gap-3.5 rounded-2xl px-2 py-2 transition-colors hover:bg-secondary has-focus-visible:ring-[3px] has-focus-visible:ring-ring/50">
                  <span className="relative mt-1 grid size-6 shrink-0 place-items-center">
                    <input
                      type="checkbox"
                      className="peer size-6 cursor-pointer appearance-none rounded-full border-2 border-input outline-none checked:border-brand checked:bg-brand"
                      checked={checked.includes(i)}
                      onChange={() => toggle(i)}
                    />
                    <Check
                      aria-hidden="true"
                      strokeWidth={3}
                      className="pointer-events-none absolute size-3.5 text-primary-foreground opacity-0 peer-checked:opacity-100"
                    />
                  </span>
                  <span className={cn(checked.includes(i) && 'text-muted-foreground line-through')}>{line}</span>
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}
      {steps.length > 0 && (
        <section aria-labelledby="cook-steps" className="flex flex-col gap-4">
          <h2 id="cook-steps" className="text-title">
            Steps
          </h2>
          <ol className="flex flex-col gap-5">
            {steps.map((line, i) => (
              <li key={i} className="flex gap-4">
                <span
                  aria-hidden="true"
                  className="grid size-9 shrink-0 place-items-center rounded-full bg-secondary text-base font-semibold"
                >
                  {i + 1}
                </span>
                <span className="pt-0.5">{line}</span>
              </li>
            ))}
          </ol>
        </section>
      )}
      {ingredients.length === 0 && steps.length === 0 && (
        <p className="text-muted-foreground">
          This recipe is kept as a photo of the card. Show the card to cook from it.
        </p>
      )}
    </article>
  )
}

// Keeps the screen on while this page is open, and asks again when the page comes back into view. Says whether it's
// on right now.
function useWakeLock() {
  const [awake, setAwake] = useState(false)
  useEffect(() => {
    if (!('wakeLock' in navigator)) return
    let lock: WakeLockSentinel | null = null
    let closed = false
    const request = async () => {
      try {
        lock = await navigator.wakeLock.request('screen')
        if (closed) return void lock.release()
        setAwake(true)
        lock.addEventListener('release', () => setAwake(false))
      } catch {
        // Not allowed right now (low battery, hidden page); reading still works.
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') void request()
    }
    void request()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      closed = true
      document.removeEventListener('visibilitychange', onVisible)
      void lock?.release()
    }
  }, [])
  return awake
}
